import {
  APIContext,
  AuthContext,
  useApi,
  getMockUserIdentities,
  MOCK_USER_TYPE,
} from '@monash/portal-frontend-common';
import { useState, useContext, useMemo } from 'react';
import c from './impersonation.module.scss';
import { LoadingIndicatorPlanet, Tabs } from '@monash/portal-react';
import RealUsers from './real-users/RealUsers';
import MockUsers from './mock-users/MockUsers';
import { isAuthorized } from '../../utilities/is-authorized';
import ACCESS_ROLES from '../../../constants/user-roles';

const Users = () => {
  const authCtx = useContext(AuthContext);
  const userRoles = authCtx?.user?.roles;
  const { isImpersonating } = useContext(APIContext);

  const [impersonatee, setImpersonatee] = useState(null);

  const startImpersonation = (user) => {
    setImpersonatee(user);
  };

  const endImpersonation = () => setImpersonatee(null);

  const { isLoading: isLoadingImpersonatee } = useApi({
    requestFn: isImpersonating,
    initialLoading: true,
    onSuccess: (response) => {
      setImpersonatee(response.currentlyImpersonating ? response : null);
    },
  });

  const { data: mockUserIdentities, isLoading: isLoadingMockUserIdentities } =
    useApi({
      requestFn: getMockUserIdentities,
      args: [{ mockUserTypes: MOCK_USER_TYPE.IMPERSONATION }],
      initialLoading: true,
    });

  const isLoading = isLoadingImpersonatee || isLoadingMockUserIdentities;

  const enrichedImpersonatee = useMemo(() => {
    if (!impersonatee) return null;

    if (!impersonatee?.isMockUser) {
      return {
        ...impersonatee,
      };
    }

    // Enrich impersonatee with "display" labels
    const correspondingMockUser = mockUserIdentities?.find(
      (mockUser) => mockUser.uid === impersonatee.uid
    );

    return {
      ...impersonatee,
      display: correspondingMockUser?.display,
    };
  }, [impersonatee, mockUserIdentities]);

  const sortedMockUserIdentities = useMemo(() => {
    return mockUserIdentities?.toSorted((a, b) => {
      const createdAtA = a.createdAt;
      const createdAtB = b.createdAt;
      if (createdAtA < createdAtB) {
        return -1;
      } else {
        return 1;
      }
    });
  }, [mockUserIdentities]);

  const VIEWS = [
    {
      description:
        'Impersonate a different user to handle support problems or to see how their UI looks.',
      heading: 'Student by ID',
      render: (props) => (
        <RealUsers
          currentlyImpersonating={
            !enrichedImpersonatee?.isMockUser && enrichedImpersonatee
          }
          startImpersonation={startImpersonation}
          endImpersonation={endImpersonation}
          {...props}
        />
      ),
      allowedRoles: [ACCESS_ROLES.IMPERSONATION],
    },
    {
      description:
        'View the student portal applications from the perspective of a student — please select a student option.',
      heading: 'Student by type',
      render: (props) => (
        <MockUsers
          mockUserIdentities={sortedMockUserIdentities}
          currentlyImpersonating={
            enrichedImpersonatee?.isMockUser && enrichedImpersonatee
          }
          startImpersonation={startImpersonation}
          endImpersonation={endImpersonation}
          {...props}
        />
      ),
      allowedRoles: [
        ACCESS_ROLES.IMPERSONATION,
        ACCESS_ROLES.MOCK_IMPERSONATION,
      ],
    },
  ];

  const allowedViews = VIEWS.filter((view) =>
    isAuthorized(userRoles, view.allowedRoles)
  );
  const hasMultipleViews = allowedViews.length > 1;
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const ViewRenderer = hasMultipleViews
    ? allowedViews[selectedTabIndex].render
    : allowedViews[0].render;
  const description = hasMultipleViews
    ? allowedViews[selectedTabIndex].description
    : allowedViews[0].description;

  if (isLoading) {
    return (
      <div className={c.container}>
        <h1 className={c.title} tabIndex={-1}>
          Users
        </h1>

        <LoadingIndicatorPlanet />
      </div>
    );
  }

  return (
    <div className={c.container}>
      <div className={c.header}>
        <h1 tabIndex={-1}>Users</h1>
        <p>{description}</p>
      </div>

      {hasMultipleViews ? (
        <div className={c.tabsContainer}>
          <Tabs
            tabs={allowedViews.map((view) => view.heading)}
            selected={selectedTabIndex}
            onChange={(i) => {
              setSelectedTabIndex(i);
            }}
          />
          <ViewRenderer />
        </div>
      ) : (
        <ViewRenderer />
      )}
    </div>
  );
};

export default Users;
